import { useCallback, useEffect, useMemo, useState } from 'react';
import useSnackbar from './useCustomSnackbar';
import { dispatch } from '../redux/store';
import { cancelAppointment } from '../redux/slices/lead';
import { getAppointment } from '../redux/slices/lead/appointmentThunks';
import { getAppointmentCancellationAction } from '../utils/appointmentUtils';
import useAppointmentRequestScope from './useAppointmentRequestScope';

const idKey = (value) => String(value);

export default function useAppointmentCancellation({ practiceId, leadId, appointments }) {
    const { enqueueSnackbar } = useSnackbar();
    const requestScope = useAppointmentRequestScope(practiceId, leadId);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [localStates, setLocalStates] = useState({});

    // Coerce, do not trust. A destructuring default (`appointments = []`) only
    // fires on undefined, and every caller passes whatever its state holder was
    // initialised to - which is frequently not an array:
    //   OverView.jsx:402/404       useState('')  -> '' reached the Map below
    //   TreatmentAccordion.jsx:52  useState(0)   -> 0  reached the Map below
    // Both slipped past the default and threw "not a function" inside this
    // useMemo. Because that runs during RENDER, React unmounted the whole
    // lead-detail tree: not a degraded widget, a white screen on every lead.
    //
    // Fixing it here rather than at the four call sites is deliberate. '' and 0
    // are FALSY and the callers gate rendering on that truthiness
    // (`if (leadAppointment)`, `{pastAppointment && pastAppointment?.length > 0}`),
    // so changing their initialisers to [] would flip empty-state rendering -
    // a behavioural change that needs its own testing, not a hotfix.
    const appointmentList = useMemo(
        () => (Array.isArray(appointments) ? appointments : []),
        [appointments],
    );

    const appointmentById = useMemo(() => new Map(
        appointmentList.map((appointment) => [idKey(appointment.id), appointment]),
    ), [appointmentList]);

    useEffect(() => {
        setSelectedAppointment(null);
        setIsCancelling(false);
    }, [practiceId, leadId]);

    useEffect(() => {
        setLocalStates((current) => {
            const next = { ...current };
            let changed = false;
            Object.keys(next).forEach((appointmentId) => {
                const appointment = appointmentById.get(appointmentId);
                const cancellationState = appointment?.dentally?.cancellation_state;
                if (!appointment || appointment.status !== 'BOOKED'
                    || ['CANCELLED', 'UNKNOWN'].includes(cancellationState)
                    || (cancellationState === 'ACTIVE' && next[appointmentId] !== 'retry')) {
                    delete next[appointmentId];
                    changed = true;
                } else if (cancellationState === 'PENDING' && next[appointmentId] !== 'retry') {
                    next[appointmentId] = 'retry';
                    changed = true;
                }
            });
            appointmentList.forEach((appointment) => {
                const key = idKey(appointment.id);
                if (appointment.status === 'BOOKED'
                    && appointment.dentally?.cancellation_state === 'IN_PROGRESS'
                    && !next[key]) {
                    next[key] = 'pending';
                    changed = true;
                }
            });
            return changed ? next : current;
        });
    }, [appointmentById, appointmentList]);

    const updateLocalState = useCallback((appointmentId, state) => {
        setLocalStates((current) => {
            const key = idKey(appointmentId);
            const next = { ...current };
            if (state) next[key] = state;
            else delete next[key];
            return next;
        });
    }, []);

    const handleResult = useCallback((appointmentId, result) => {
        setIsCancelling(false);
        setSelectedAppointment(null);
        if (result.ambiguous || (result.refreshAvailable && !result.retryable)) {
            updateLocalState(appointmentId, 'refresh');
        } else if (result.retryable) updateLocalState(appointmentId, 'refresh');
        else if (result.pending) updateLocalState(appointmentId, 'pending');
        else updateLocalState(appointmentId, null);

        let variant = 'error';
        if (result.refreshAvailable) variant = 'warning';
        else if (result.pending) variant = 'info';
        else if (result.success) variant = 'success';
        enqueueSnackbar(result.message || '', {
            variant,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
    }, [enqueueSnackbar, updateLocalState]);

    const getAction = useCallback((row) => getAppointmentCancellationAction(
        row.Status,
        row.dentally,
        localStates[idKey(row.appointment_id)],
        row.calcom,
    ), [localStates]);

    useEffect(() => {
        if (!selectedAppointment) return;
        const currentAppointment = appointmentById.get(idKey(selectedAppointment.id));
        if (!currentAppointment) {
            setSelectedAppointment(null);
            return;
        }
        const currentAction = getAppointmentCancellationAction(
            currentAppointment.status,
            currentAppointment.dentally,
            localStates[idKey(selectedAppointment.id)],
            currentAppointment.calcom,
        );
        if (!['cancel', 'retry'].includes(currentAction.intent)) setSelectedAppointment(null);
    }, [appointmentById, localStates, selectedAppointment]);

    const refreshCancellation = useCallback(async (row) => {
        const appointmentId = row.appointment_id;
        updateLocalState(appointmentId, 'refreshing');
        const response = await dispatch(getAppointment(practiceId, leadId, requestScope));
        if (response === null) return;

        if (response?.status !== 200) {
            updateLocalState(appointmentId, 'refresh');
            enqueueSnackbar('Unable to refresh cancellation status. Try again.', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
            return;
        }

        const appointment = response.data?.results?.find(
            (item) => idKey(item.id) === idKey(appointmentId),
        );
        const cancellationState = appointment?.dentally?.cancellation_state;
        if (appointment?.status !== 'BOOKED'
            || appointment?.dentally?.reconciliation_required
            || ['CANCELLED', 'UNKNOWN'].includes(cancellationState)) {
            updateLocalState(appointmentId, null);
        } else if (cancellationState === 'PENDING') {
            updateLocalState(appointmentId, 'retry');
        } else if (cancellationState === 'ACTIVE') {
            updateLocalState(appointmentId, null);
        } else if (cancellationState === 'IN_PROGRESS') {
            updateLocalState(appointmentId, 'refresh');
        } else {
            updateLocalState(appointmentId, null);
        }
    }, [enqueueSnackbar, leadId, practiceId, requestScope, updateLocalState]);

    const beginAction = useCallback((row) => {
        const action = getAction(row);
        if (action.intent === 'refresh') {
            refreshCancellation(row);
            return;
        }
        if (action.intent === 'cancel' || action.intent === 'retry') {
            setSelectedAppointment({ id: row.appointment_id, dentally: row.dentally });
        }
    }, [getAction, refreshCancellation]);

    const closeConfirmation = useCallback(() => {
        if (!isCancelling) setSelectedAppointment(null);
    }, [isCancelling]);

    const confirmCancellation = useCallback(() => {
        if (isCancelling || !selectedAppointment) return;
        const { id } = selectedAppointment;
        const currentAppointment = appointmentById.get(idKey(id));
        if (!currentAppointment) {
            setSelectedAppointment(null);
            return;
        }
        const currentAction = getAppointmentCancellationAction(
            currentAppointment.status,
            currentAppointment.dentally,
            localStates[idKey(id)],
            currentAppointment.calcom,
        );
        if (!['cancel', 'retry'].includes(currentAction.intent)) {
            setSelectedAppointment(null);
            return;
        }
        setIsCancelling(true);
        dispatch(cancelAppointment(
            practiceId,
            leadId,
            id,
            (result) => handleResult(id, result),
            currentAppointment.dentally,
            requestScope,
        ));
    }, [appointmentById, handleResult, isCancelling, leadId, localStates, practiceId, requestScope, selectedAppointment]);

    return {
        beginAction,
        closeConfirmation,
        confirmCancellation,
        getAction,
        isCancelling,
        isConfirmationOpen: Boolean(selectedAppointment),
    };
}
